import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Card,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  AlertTitle,
  Grid,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward,
  ArrowDownward,
  Save as SaveIcon,
} from '@mui/icons-material';
import api from '../axiosConfig';

const emptyPackage = () => ({
  name: '',
  description: '',
  price_override: '',
  deposit_override: '',
  slots: [],
});

const newSlot = (order) => ({
  slot_type: 'fixed',
  display_label: '',
  display_order: order,
  options: [{ service_id: '', display_order: 0 }],
});

const PackageEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id && id !== 'new');

  const [pkg, setPkg] = useState(emptyPackage());
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);

  const loadServices = useCallback(async () => {
    const res = await api.get('/services');
    const list = res.data.services || res.data || [];
    setServices(list.filter((s) => !s.is_deleted));
  }, []);

  const loadPackage = useCallback(async () => {
    if (!isEdit) return;
    const res = await api.get(`/packages/${id}`);
    const p = res.data.package;
    setPkg({
      name: p.name || '',
      description: p.description || '',
      price_override: p.price_override ?? '',
      deposit_override: p.deposit_override ?? '',
      slots: (p.slots || []).map((slot, i) => ({
        slot_type: slot.slot_type,
        display_label: slot.display_label || '',
        display_order: slot.display_order ?? i,
        options: (slot.options || []).map((opt, j) => ({
          service_id: opt.service_id,
          display_order: opt.display_order ?? j,
        })),
      })),
    });
  }, [id, isEdit]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([loadServices(), loadPackage()]);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadServices, loadPackage]);

  const updateField = (field, value) => setPkg((p) => ({ ...p, [field]: value }));

  const addSlot = () => {
    setPkg((p) => ({ ...p, slots: [...p.slots, newSlot(p.slots.length)] }));
  };

  const removeSlot = (idx) => {
    setPkg((p) => {
      const slots = p.slots
        .filter((_, i) => i !== idx)
        .map((s, i) => ({ ...s, display_order: i }));
      return { ...p, slots };
    });
  };

  const moveSlot = (idx, direction) => {
    setPkg((p) => {
      const target = idx + direction;
      if (target < 0 || target >= p.slots.length) return p;
      const slots = [...p.slots];
      [slots[idx], slots[target]] = [slots[target], slots[idx]];
      return { ...p, slots: slots.map((s, i) => ({ ...s, display_order: i })) };
    });
  };

  const updateSlot = (idx, field, value) => {
    setPkg((p) => {
      const slots = [...p.slots];
      const slot = { ...slots[idx], [field]: value };
      if (field === 'slot_type' && value === 'fixed' && slot.options.length > 1) {
        slot.options = [slot.options[0]];
      }
      if (field === 'slot_type' && value === 'choice' && slot.options.length < 2) {
        slot.options = [...slot.options, { service_id: '', display_order: slot.options.length }];
      }
      slots[idx] = slot;
      return { ...p, slots };
    });
  };

  const addOption = (slotIdx) => {
    setPkg((p) => {
      const slots = [...p.slots];
      const slot = { ...slots[slotIdx] };
      slot.options = [...slot.options, { service_id: '', display_order: slot.options.length }];
      slots[slotIdx] = slot;
      return { ...p, slots };
    });
  };

  const removeOption = (slotIdx, optIdx) => {
    setPkg((p) => {
      const slots = [...p.slots];
      const slot = { ...slots[slotIdx] };
      slot.options = slot.options
        .filter((_, i) => i !== optIdx)
        .map((o, i) => ({ ...o, display_order: i }));
      slots[slotIdx] = slot;
      return { ...p, slots };
    });
  };

  const moveOption = (slotIdx, optIdx, direction) => {
    setPkg((p) => {
      const slots = [...p.slots];
      const slot = { ...slots[slotIdx] };
      const opts = [...slot.options];
      const target = optIdx + direction;
      if (target < 0 || target >= opts.length) return p;
      [opts[optIdx], opts[target]] = [opts[target], opts[optIdx]];
      slot.options = opts.map((o, i) => ({ ...o, display_order: i }));
      slots[slotIdx] = slot;
      return { ...p, slots };
    });
  };

  const updateOption = (slotIdx, optIdx, serviceId) => {
    setPkg((p) => {
      const slots = [...p.slots];
      const slot = { ...slots[slotIdx] };
      const opts = [...slot.options];
      opts[optIdx] = { ...opts[optIdx], service_id: serviceId };
      slot.options = opts;
      slots[slotIdx] = slot;
      return { ...p, slots };
    });
  };

  const validateLocal = () => {
    if (!pkg.name.trim()) return 'Package name is required';
    if (pkg.slots.length === 0) return 'At least one slot is required';
    for (const [i, slot] of pkg.slots.entries()) {
      if (!slot.display_label.trim()) return `Slot ${i + 1}: label is required`;
      if (slot.slot_type === 'fixed' && slot.options.length !== 1) {
        return `Slot "${slot.display_label}" (fixed): must have exactly 1 option`;
      }
      if (slot.slot_type === 'choice' && slot.options.length < 2) {
        return `Slot "${slot.display_label}" (choice): needs at least 2 options`;
      }
      const seen = new Set();
      for (const opt of slot.options) {
        if (!opt.service_id) return `Slot "${slot.display_label}": every option needs a service`;
        if (seen.has(String(opt.service_id))) {
          return `Slot "${slot.display_label}": service appears more than once`;
        }
        seen.add(String(opt.service_id));
      }
    }
    return null;
  };

  const handleSave = async () => {
    setError(null);
    setWarnings([]);
    const localErr = validateLocal();
    if (localErr) {
      setError(localErr);
      return;
    }

    const payload = {
      name: pkg.name.trim(),
      description: pkg.description.trim() || null,
      price_override: pkg.price_override === '' ? null : pkg.price_override,
      deposit_override: pkg.deposit_override === '' ? null : pkg.deposit_override,
      slots: pkg.slots.map((s, i) => ({
        slot_type: s.slot_type,
        display_label: s.display_label.trim(),
        display_order: i,
        options: s.options.map((o, j) => ({
          service_id: Number(o.service_id),
          display_order: j,
        })),
      })),
    };

    setSaving(true);
    try {
      const res = isEdit
        ? await api.put(`/packages/${id}`, payload)
        : await api.post('/packages', payload);
      setWarnings(res.data.warnings || []);
      if (!isEdit) {
        navigate(`/solo/packages/${res.data.package.id}`, { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
        }}
      >
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* Compact Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => navigate('/solo/packages')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" fontWeight="600">
            {isEdit ? 'Edit Package' : 'New Package'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => navigate('/solo/packages')} disabled={saving} size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Package'}
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarnings([])}>
              <AlertTitle>Saved with warnings</AlertTitle>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </Box>
            </Alert>
          )}

          {/* Package details */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              Package Details
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Package Name"
                  value={pkg.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="e.g., Colour and Cut Package"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={pkg.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Optional description shown to clients"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Price Override"
                  value={pkg.price_override}
                  onChange={(e) => updateField('price_override', e.target.value)}
                  helperText="Blank = sum of parts"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Deposit Override"
                  value={pkg.deposit_override}
                  onChange={(e) => updateField('deposit_override', e.target.value)}
                  helperText="Blank = use account rule"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Slots */}
          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight="600">
                Slots
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addSlot}>
                Add Slot
              </Button>
            </Box>

            {pkg.slots.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 4 }}
              >
                A package needs at least one slot. Add one to get started.
              </Typography>
            )}

            {pkg.slots.map((slot, sIdx) => (
              <Card variant="outlined" key={sIdx} sx={{ p: 2, mb: 2 }}>
                {/* Slot header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => moveSlot(sIdx, -1)}
                        disabled={sIdx === 0}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => moveSlot(sIdx, 1)}
                        disabled={sIdx === pkg.slots.length - 1}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography variant="body1" fontWeight="600" sx={{ ml: 1 }}>
                    Slot {sIdx + 1}
                  </Typography>
                  <Tooltip title="Remove slot">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeSlot(sIdx)}
                      sx={{ ml: 'auto' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={slot.slot_type}
                        label="Type"
                        onChange={(e) => updateSlot(sIdx, 'slot_type', e.target.value)}
                      >
                        <MenuItem value="fixed">Fixed (one specific service)</MenuItem>
                        <MenuItem value="choice">Choice (client picks one)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Display Label"
                      value={slot.display_label}
                      onChange={(e) => updateSlot(sIdx, 'display_label', e.target.value)}
                      placeholder="e.g., Colour service, Finish"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight="600">
                      Options
                    </Typography>
                    {slot.slot_type === 'choice' && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => addOption(sIdx)}
                      >
                        Add Option
                      </Button>
                    )}
                  </Box>

                  {slot.options.map((opt, oIdx) => (
                    <Box
                      key={oIdx}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
                    >
                      {slot.slot_type === 'choice' && (
                        <>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => moveOption(sIdx, oIdx, -1)}
                                disabled={oIdx === 0}
                              >
                                <ArrowUpward fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => moveOption(sIdx, oIdx, 1)}
                                disabled={oIdx === slot.options.length - 1}
                              >
                                <ArrowDownward fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      <FormControl fullWidth size="small">
                        <Select
                          value={opt.service_id}
                          onChange={(e) => updateOption(sIdx, oIdx, e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            <em>— Select service —</em>
                          </MenuItem>
                          {services.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.name}
                              {s.default_price ? ` (£${Number(s.default_price).toFixed(2)})` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {slot.slot_type === 'choice' && slot.options.length > 2 && (
                        <Tooltip title="Remove option">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeOption(sIdx, oIdx)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                </Box>
              </Card>
            ))}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default PackageEditor;